import { useEffect } from "react";
import { Link } from "react-router-dom";
import SiteNav from "@/components/feature/SiteNav";
import SiteFooter from "@/components/feature/SiteFooter";
import { site, categories } from "@/mocks/siteData";

const aboutHero =
  "https://readdy.ai/api/search-image?query=low%20wide%20view%20of%20ancient%20stone%20carvings%20and%20bronze%20vessels%20emerging%20from%20darkness%20with%20warm%20amber%20rim%20lighting%2C%20fog%20and%20dust%20in%20light%20beam%2C%20mysterious%20museum%20vault%20atmosphere%2C%20golden%20and%20burnt%20umber%20palette%2C%20painterly%20editorial%20photography%2C%20high%20detail&width=1600&height=760&seq=about-hero&orientation=landscape";

export default function AboutPage() {
  useEffect(() => {
    document.title = "关于我们 - 猎奇网历史探秘内容站";
  }, []);

  const aboutLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于猎奇网",
    description: site.intro,
    inLanguage: "zh-CN",
    mainEntity: { "@type": "Organization", name: site.name },
  };

  const values = [
    { icon: "ri-quill-pen-line", t: "考据为本", d: "每一条线索都尽量回到一手文献、考古简报与可信来源，标注出处与不确定处。" },
    { icon: "ri-footprint-line", t: "实地求证", d: "把民间旧谈与传世史料放回地面，用物证、遗迹与地理去校准传说。" },
    { icon: "ri-question-line", t: "保留争议", d: "面对分歧，并列不同说法而不急于定论，把判断的空间留给你。" },
    { icon: "ri-eye-off-line", t: "尊重沉默", d: "没有实据就坦诚标注‘存疑’，不把臆测包装成结论，也不消费历史的悲欢。" },
  ];

  const milestones = [
    { k: "我们相信", v: "历史很少真正消失，它只是改换了藏身之处——在边角处、在墓室中、在口耳相传的坐标里。" },
    { k: "我们不做什么", v: "不为猎奇而夸张细节，不虚构地点与资质，不把传说讲成铁证。" },
    { k: "我们想做成什么", v: "一个可以慢慢逛的历史档案室：既能顺藤摸瓜追线索，也能为一句旧谈找它的地理原型。" },
  ];

  const voices = [
    {
      name: "考古专业读者 · 石桥",
      text: "把发掘简报里的术语转译成能读懂的现场，却又没有把证据的严谨性丢掉，这一点很难得。",
    },
    {
      name: "普通爱好者 · 遥望长安",
      text: "很多历史文章读完就忘了，这里的栏目能让人真的想顺着一座城、一口井去查更多资料。",
    },
    {
      name: "学生读者 · 阿澄",
      text: "讲‘存疑’和‘别急着下结论’的几篇，教会我的不只是历史，还有看待信息的方式。",
    },
  ];

  return (
    <div className="bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutLd) }} />
      <SiteNav />

      <section className="relative overflow-hidden bg-foreground-950 text-background-50">
        <img
          src={aboutHero}
          alt="猎奇网关于我们 · 从暗影中浮现的古代石刻"
          className="absolute inset-0 h-full w-full object-cover object-top opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-foreground-950/55 to-foreground-950"></div>
        <div className="relative mx-auto max-w-[1400px] px-4 pb-12 pt-28 md:px-6 md:pt-40">
          <p className="text-xs font-semibold tracking-[0.22em] text-primary-300">ABOUT US</p>
          <h1 className="mt-2 max-w-3xl text-balance font-heading text-4xl font-black leading-tight md:text-5xl">
            关于{site.name}
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-100/80 md:text-base">
            {site.intro}
          </p>
        </div>
      </section>

      <main>
        {/* positioning + values */}
        <section className="mx-auto max-w-[1400px] px-4 py-14 md:px-6 md:py-20">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <p className="mb-2 text-xs font-semibold tracking-[0.2em] text-primary-700">WHAT WE ARE</p>
              <h2 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">
                一份写给好奇心者的历史档案
              </h2>
              <div className="mt-6 space-y-4 text-[15px] leading-[1.9] text-foreground-700">
                <p>
                  {site.name}不是把考古报告搬上网页，而是把田野、纸张与传说的线索，翻译成一段可以跟进的故事。
                  我们相信，文物背后的真相，往往藏在史书一句带过的地方、墓室一条不起眼的耳道里。
                </p>
                <p>
                  我们的读者里，有考古专业的学生，也有只是对古物与旧谈着迷的普通人。我们尽力让专业内容不摆架子，
                  也让科普语言不失分寸——这是{site.name}自创立以来一直坚持的内容原则。
                </p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {values.map((v) => (
                <div key={v.t} className="rounded-xl border border-background-200 bg-background-100 p-5">
                  <i className={`${v.icon} text-2xl text-primary-600`} aria-hidden="true"></i>
                  <h3 className="mt-3 font-heading text-base font-bold text-foreground-900">{v.t}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">{v.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* milestones dark band */}
        <section className="bg-foreground-950 text-background-100">
          <div className="mx-auto max-w-[1400px] px-4 py-14 md:px-6 md:py-20">
            <p className="mb-8 text-xs font-semibold tracking-[0.2em] text-primary-300">HOW WE THINK</p>
            <div className="grid gap-6 md:grid-cols-3">
              {milestones.map((m) => (
                <div key={m.k} className="rounded-xl border border-background-50/10 bg-background-50/5 p-6">
                  <h3 className="font-heading text-lg font-bold text-primary-300">{m.k}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-background-100/75">{m.v}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* voices */}
        <section className="mx-auto max-w-[1400px] px-4 py-14 md:px-6 md:py-20">
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <p className="mb-2 text-xs font-semibold tracking-[0.2em] text-primary-700">VOICES</p>
            <h2 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">读者怎么说</h2>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {voices.map((v) => (
              <figure key={v.name} className="flex flex-col rounded-xl border border-background-200 bg-background-50 p-6">
                <i className="ri-double-quotes-l text-3xl text-primary-400" aria-hidden="true"></i>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  “{v.text}”
                </blockquote>
                <figcaption className="mt-5 border-t border-background-200 pt-3 text-xs text-foreground-500">
                  {v.name}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* categories + contact */}
        <section className="bg-background-100">
          <div className="mx-auto grid max-w-[1400px] gap-10 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-2">
            <div>
              <p className="mb-2 text-xs font-semibold tracking-[0.2em] text-primary-700">EXPLORE</p>
              <h2 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">从这里开始你的探秘</h2>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/explore/${c.slug}`}
                    className="group flex items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3 transition-colors hover:border-primary-300"
                  >
                    <span className="flex items-center gap-2 text-sm font-medium text-foreground-800">
                      <i className="ri-arrow-right-circle-line text-primary-600" aria-hidden="true"></i>
                      {c.name}
                    </span>
                    <i className="ri-arrow-right-line text-foreground-400 transition-transform group-hover:translate-x-1 group-hover:text-primary-600" aria-hidden="true"></i>
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold tracking-[0.2em] text-primary-700">CONTACT</p>
              <h2 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">联系编辑部</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                发现史料疑点、手上有值得追踪的线索，或想聊聊选题，欢迎来信。编辑部会在工作日内回复。
              </p>
              <div className="mt-6 space-y-3 rounded-xl border border-background-200 bg-background-50 p-6">
                <a
                  href={`mailto:${site.contact.email}`}
                  className="flex items-center gap-3 text-sm text-foreground-800 transition-colors hover:text-primary-700"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50">
                    <i className="ri-mail-line" aria-hidden="true"></i>
                  </span>
                  投稿与反馈 · {site.contact.email}
                </a>
                <div className="flex items-center gap-3 text-sm text-foreground-800">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-500 text-background-50">
                    <i className="ri-weibo-line" aria-hidden="true"></i>
                  </span>
                  官方微博 · @{site.contact.weibo}
                </div>
                <div className="flex items-center gap-3 text-sm text-foreground-800">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                    <i className="ri-time-line" aria-hidden="true"></i>
                  </span>
                  更新节奏 · 每周一、周四
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}